import {
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { useQueryClient } from "@tanstack/react-query";

import { AuthContext } from "@/auth/AuthContext";
import {
	authErrorHandler,
	drainPendingAuthError,
	resetAuthErrorHandler,
} from "@/auth/authErrorBridge";

import { SATOSPortal } from "@/data-adapter/SATOSPortal";

import { User } from "@/model/User/User";

import { ApplicationQueryKeys } from "@/service/ApplicationQueries";
import { TaskQueryKeys } from "@/service/TaskQueries";

import { HttpError, isHttpError } from "@/shared/api/HttpError";

interface AuthProviderProps {
	children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
	const contextValue = useContextChange();
	return (
		<AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
	);
}

const useContextChange = () => {
	const [isAuthFlowCompleted, setIsAuthFlowCompleted] = useState(false);
	const [isFetching, setIsFetching] = useState(false);
	const [user, setUser] = useState<User | null>(null);
	const [sessionExpired, setSessionExpired] = useState(false);
	const [accessDenied, setAccessDenied] = useState(false);
	const [authError, setAuthError] = useState(false);
	const hasAuthenticatedOnceRef = useRef(false);
	const sessionExpiredRef = useRef(false);
	const queryClient = useQueryClient();

	const login = useCallback(
		(postLoginRedirectUri: string, scopesToConsent: Array<string>) => {
			let url = "/api/auth/login";

			const searchParams = new URLSearchParams({});

			if (postLoginRedirectUri) {
				searchParams.append(
					"postLoginRedirectUri",
					encodeURIComponent(postLoginRedirectUri),
				);
			}

			if (scopesToConsent) {
				searchParams.append("scopesToConsent", scopesToConsent.join(" "));
			}

			url = `${url}?${searchParams.toString()}`;
			window.location.replace(url);
		},
		[],
	);

	useEffect(() => {
		let isMounted = true;

		const checkAuth = async () => {
			if (!isMounted) return;
			setIsFetching(true);

			try {
				const userData = await queryClient.ensureQueryData<User>({
					queryKey: [ApplicationQueryKeys.user],
					queryFn: () => SATOSPortal.GetCurrentUser(),
					staleTime: 5000,
				});

				await Promise.all([
					queryClient.ensureQueryData({
						queryKey: [
							ApplicationQueryKeys.user,
							ApplicationQueryKeys.userCompanyConfiguration,
						],
						queryFn: () => SATOSPortal.UserCompanyConfiguration(),
						staleTime: 5000,
					}),
					queryClient.ensureQueryData({
						queryKey: [ApplicationQueryKeys.appConfiguration],
						queryFn: () => SATOSPortal.GetApplicationConfiguration(),
						staleTime: 30000,
					}),
					queryClient.ensureQueryData({
						queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
						queryFn: () => SATOSPortal.GetProcedureUpdate(false),
						staleTime: 1000,
					}),
				]);

				if (isMounted) {
					sessionExpiredRef.current = false;
					setUser(userData);
					setSessionExpired(false);
					hasAuthenticatedOnceRef.current = true;
				}
			} catch (error) {
				if (!isMounted) return;
				if (isHttpError(error) && error.status === 403) {
					setAccessDenied(true);
				} else if (isHttpError(error) && error.status !== 401) {
					setAuthError(true);
				} else if (!isHttpError(error)) {
					console.error("Unexpected authentication check failure", error);
					setAuthError(true);
				}
			} finally {
				if (isMounted) {
					setIsFetching(false);
					setIsAuthFlowCompleted(true);
				}
			}
		};

		void checkAuth();

		return () => {
			isMounted = false;
		};
	}, [queryClient]);

	useEffect(() => {
		const handle = (_error: HttpError) => {
			if (!hasAuthenticatedOnceRef.current) return;
			if (sessionExpiredRef.current) return;

			sessionExpiredRef.current = true;
			setSessionExpired(true);
			setUser(null);
			setIsFetching(false);
			setIsAuthFlowCompleted(true);
		};

		authErrorHandler.current = handle;

		if (hasAuthenticatedOnceRef.current) {
			const queued = drainPendingAuthError();
			if (queued) handle(queued);
		}

		return resetAuthErrorHandler;
	}, []);

	return useMemo(
		() => ({
			user,
			isFetching,
			isAuthFlowCompleted,
			login,
			sessionExpired,
			accessDenied,
			authError,
		}),
		[user, isFetching, isAuthFlowCompleted, login, sessionExpired, accessDenied, authError],
	);
};
